import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { auth } from '../../../lib/firebase';
import { useToast } from '../../../hooks/use-toast';
import AIVoiceMetrics from '../Analytics/AIVoiceMetrics';
import { tenantHeaders } from '../../../lib/tenantSession';
import {
  TrendingUp, Users, Target, BarChart3, AlertTriangle, CheckCircle,
  Crown, Activity, Shield, Zap, Bot, Phone, RefreshCw,
  Star, Clock, MessageSquare, PhoneMissed
} from 'lucide-react';

const ExecutiveDashboard = () => {
  const { currentBusiness } = useBusiness();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);

  const [execData, setExecData] = useState(null);
  const [bizData, setBizData] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [voiceData, setVoiceData] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE || '';

  const fetchAll = useCallback(async () => {
    const businessId = currentBusiness?.brandId;
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Use the live, auto-refreshed Firebase ID token; fall back to the cached one only if the SDK
      // has no current user. The cached crm_access_token goes stale after ~1h and caused 401s.
      const token = (auth.currentUser ? await auth.currentUser.getIdToken() : null) || localStorage.getItem('crm_access_token') || '';
      const h = { Authorization: `Bearer ${token}`, ...tenantHeaders() };
      const [e, b, a, v] = await Promise.allSettled([
        fetch(`${backendUrl}/api/v1/analytics/executive/${businessId}`, { headers: h }),
        fetch(`${backendUrl}/api/v1/analytics/business/${businessId}?period=${selectedPeriod}`, { headers: h }),
        fetch(`${backendUrl}/api/v1/analytics/ai-metrics/${businessId}?period=${selectedPeriod}`, { headers: h }),
        fetch(`${backendUrl}/api/v1/ai-voice/business-stats/${businessId}`, { headers: h }),
      ]);
      if (e.status === 'fulfilled' && e.value.ok) setExecData(await e.value.json());
      if (b.status === 'fulfilled' && b.value.ok) setBizData(await b.value.json());
      if (a.status === 'fulfilled' && a.value.ok) setAiData(await a.value.json());
      if (v.status === 'fulfilled' && v.value.ok) setVoiceData(await v.value.json());
    } catch (err) {
      toast({ title: t('common.error'), description: t('pg.exec.failedLoad'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.brandId, selectedPeriod, backendUrl]);

  useEffect(() => {
    if (currentBusiness?.brandId) fetchAll();
    else setLoading(false);
  }, [currentBusiness?.brandId, selectedPeriod]);

  const fmt = (val, suffix = '') => val != null ? `${val}${suffix}` : '—';
  const fmtRate = (val) => val != null ? `${val}%` : '—';
  const fmtCsat = (val) => val != null ? `${val}/5` : '—';

  if (loading) {
    return (
      <div className="flex-1 h-full flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Crown className="h-10 w-10 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('pg.exec.title')}</h1>
            <p className="text-gray-600">{t('pg.exec.liveMetrics')} {currentBusiness?.name || t('pcb.exec.yourBusiness')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">{t('pg.exec.days7')}</SelectItem>
              <SelectItem value="30d">{t('pg.exec.days30')}</SelectItem>
              <SelectItem value="90d">{t('pg.exec.days90')}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Top-line operational KPIs */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-cyan-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">{t('pg.exec.totalCustomers')}</p>
                <p className="text-3xl font-bold text-blue-900">{fmt(execData?.total_customers)}</p>
                <p className="text-xs text-blue-600 mt-1">{t('pg.exec.registeredPlatform')}</p>
              </div>
              <Users className="h-12 w-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">{t('pg.exec.aiResolutionRate')}</p>
                <p className="text-3xl font-bold text-green-900">{fmtRate(bizData?.ai_resolution_rate)}</p>
                <p className="text-xs text-green-600 mt-1">{t('pg.exec.chatAutoResolved')}</p>
              </div>
              <Bot className="h-12 w-12 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-800">{t('pg.exec.customerCsat')}</p>
                <p className="text-3xl font-bold text-purple-900">{fmtCsat(bizData?.customer_satisfaction)}</p>
                <p className="text-xs text-purple-600 mt-1">
                  {bizData?.csat_response_count ? `${bizData.csat_response_count} ${t('pcb.exec.responses')}` : t('pg.exec.noRatingsYet')}
                </p>
              </div>
              <Star className="h-12 w-12 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-r from-orange-50 to-red-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-800">{t('pg.exec.escalationRate')}</p>
                <p className="text-3xl font-bold text-orange-900">{fmtRate(bizData?.escalation_rate)}</p>
                <p className="text-xs text-orange-600 mt-1">{t('pcb.exec.ticketsRequiringHuman')}</p>
              </div>
              <AlertTriangle className="h-12 w-12 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="operations" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="operations">{t('pcb.exec.tabOperations')}</TabsTrigger>
          <TabsTrigger value="ai_performance">{t('pcb.exec.tabAiPerformance')}</TabsTrigger>
          <TabsTrigger value="voice">{t('pcb.exec.tabVoiceChannel')}</TabsTrigger>
          <TabsTrigger value="service_quality">{t('pcb.exec.tabServiceQuality')}</TabsTrigger>
        </TabsList>

        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: t('pcb.exec.openTickets'), value: fmt(execData?.active_tickets), icon: BarChart3, color: 'text-blue-600', note: t('pcb.exec.openRightNow') },
              { label: t('pcb.exec.totalTickets'), value: fmt(bizData?.total_tickets), icon: CheckCircle, color: 'text-green-600', note: `${fmt(bizData?.resolved_tickets)} ${t('pcb.exec.resolved')}` },
              { label: t('pcb.exec.avgResolution'), value: bizData?.avg_resolution_time != null ? `${bizData.avg_resolution_time}h` : '—', icon: Clock, color: 'text-indigo-600', note: t('pcb.exec.createClose') },
              { label: t('pcb.exec.avgFirstResponse'), value: bizData?.avg_first_response_time != null ? `${bizData.avg_first_response_time}m` : '—', icon: Zap, color: 'text-yellow-600', note: t('pcb.exec.firstReply') },
            ].map((item, i) => (
              <Card key={i} className="shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{item.label}</p>
                      <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
                      <p className="text-xs text-gray-400 mt-1">{item.note}</p>
                    </div>
                    <item.icon className={`h-8 w-8 ${item.color} opacity-70`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Card className="shadow-sm">
              <CardContent className="p-5">
                <p className="text-sm text-gray-500">{t('pcb.exec.avgChatDuration')}</p>
                <p className="text-2xl font-bold text-teal-600">
                  {bizData?.avg_chat_duration != null ? `${bizData.avg_chat_duration}m` : '—'}
                </p>
                <p className="text-xs text-gray-400 mt-1">{t('pcb.exec.perResolvedConv')}</p>
              </CardContent>
            </Card>
            <Card className="shadow-sm">
              <CardContent className="p-5">
                <p className="text-sm text-gray-500">{t('pcb.exec.callbackRate')}</p>
                <p className="text-2xl font-bold text-yellow-600">{fmtRate(bizData?.callback_rate)}</p>
                <p className="text-xs text-gray-400 mt-1">{t('pcb.exec.escalationsPendingCallback')}</p>
              </CardContent>
            </Card>
            <Card className="shadow-sm">
              <CardContent className="p-5">
                <p className="text-sm text-gray-500">{t('pcb.exec.reopenedConversations')}</p>
                <p className={`text-2xl font-bold ${(bizData?.reopened_conversations || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {fmt(bizData?.reopened_conversations)}
                </p>
                <p className="text-xs text-gray-400 mt-1">{t('pcb.exec.requiredFollowup')}</p>
              </CardContent>
            </Card>
          </div>

          {/* Operator escalation distribution */}
          {bizData?.operator_escalation_counts?.length > 0 && (
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {t('pcb.exec.operatorEscalationDist')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {bizData.operator_escalation_counts.slice(0, 8).map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b last:border-0">
                      {/* DR-008: show the backend-enriched operator NAME, never the raw staff_id UUID. */}
                      <span className="text-sm text-gray-700 truncate flex-1">{item.operator_name || 'Unknown staff member'}</span>
                      <Badge variant="secondary">{item.count} {t('pcb.exec.assigned')}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* AI Performance Tab */}
        <TabsContent value="ai_performance" className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: t('pcb.exec.aiConversations'), value: fmt(aiData?.conversations_handled), icon: MessageSquare, color: 'text-blue-600' },
              { label: t('pcb.exec.autoResolution'), value: fmtRate(aiData?.auto_resolution_rate), icon: CheckCircle, color: 'text-green-600' },
              { label: t('pcb.exec.avgAiConfidence'), value: aiData?.avg_confidence != null ? `${Math.round(aiData.avg_confidence * 100)}%` : '—', icon: Target, color: 'text-purple-600' },
              { label: t('pcb.exec.openaiFallbackRate'), value: fmtRate(bizData?.openai_usage_rate), icon: Zap, color: 'text-orange-600' },
            ].map((item, i) => (
              <Card key={i} className="shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{item.label}</p>
                      <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
                    </div>
                    <item.icon className={`h-8 w-8 ${item.color} opacity-70`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <Shield className="w-4 h-4 inline mr-2" />
            {t('pcb.exec.financialMetricsNote')}
          </div>
        </TabsContent>

        {/* Voice Channel Tab */}
        <TabsContent value="voice" className="space-y-6">
          <AIVoiceMetrics compact={false} />
        </TabsContent>

        {/* Service Quality Tab */}
        <TabsContent value="service_quality" className="space-y-6">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="shadow-sm">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Star className="w-4 h-4 text-yellow-500" />{t('pcb.exec.csatScore')}</CardTitle></CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-purple-600">{fmtCsat(bizData?.customer_satisfaction)}</p>
                <p className="text-sm text-gray-500 mt-1">{t('pcb.exec.avgCustomerRating')}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {bizData?.csat_response_count > 0
                    ? t('pcb.exec.basedOnRatings', { count: bizData.csat_response_count })
                    : t('pcb.exec.noRatingsCollected')}
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-500" />{t('pcb.exec.resolutionHealth')}</CardTitle></CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-green-600">{fmtRate(bizData?.ai_resolution_rate)}</p>
                <p className="text-sm text-gray-500 mt-1">{t('pcb.exec.aiResolvedNoHuman')}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {bizData?.resolved_tickets != null
                    ? t('pcb.exec.ticketsResolvedOf', { resolved: bizData.resolved_tickets, total: bizData.total_tickets })
                    : t('pcb.exec.noData')}
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><PhoneMissed className="w-4 h-4 text-red-500" />{t('pcb.exec.callbackSla')}</CardTitle></CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-red-600">{fmt(voiceData?.callback_sla_breaches)}</p>
                <p className="text-sm text-gray-500 mt-1">{t('pcb.exec.slaBreaches')}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {voiceData?.pending_callbacks != null
                    ? t('pcb.exec.pendingOverdue', { pending: voiceData.pending_callbacks, overdue: voiceData.overdue_callbacks })
                    : t('pcb.exec.noVoiceData')}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <Activity className="w-4 h-4 inline mr-2" />
            {t('pcb.exec.surveyMetricsNote')}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExecutiveDashboard;
